import React from "react";
import {
  Box,
  Typography,
  TextField,
  MenuItem,
  Button,
  CircularProgress,
} from "@mui/material";
import { useQuery, gql } from "@apollo/client";
import {
  GET_ACADEMIC_YEARS,
  GET_ALL_EXAM_PAPERS_DELIVERY,
  GET_BATCHES,
  GET_BOARDS,
  GET_CLASSES,
} from "utils/graphql/services/ai-evaluation/queries";

interface FilterInputsProps {
  examPaperDeliveryId: string;
  setExamPaperDeliveryId: (value: string) => void;
  collegeId: string;
  setCollegeId: (value: string) => void;
  academicYearId: string;
  setAcademicYearId: (value: string) => void;
  batchId: string;
  setBatchId: (value: string) => void;
  classId: string;
  setClassId: (value: string) => void;
  boardId: string;
  setBoardId: (value: string) => void;
  isArchieved: string;
  setIsArchieved: (value: string) => void;
  createdFrom: string;
  setCreatedFrom: (value: string) => void;
  createdTill: string;
  setCreatedTill: (value: string) => void;
  updatedFrom: string;
  setUpdatedFrom: (value: string) => void;
  updatedTill: string;
  setUpdatedTill: (value: string) => void;
  handleApplyFilter: () => void;
  handleClearFilter: () => void;
}

const GET_COLLEGES = gql`
  query GetColleges {
    colleges {
      id
      name
    }
  }
`;

const FilterInputs: React.FC<FilterInputsProps> = ({
  examPaperDeliveryId,
  setExamPaperDeliveryId,
  collegeId,
  setCollegeId,
  academicYearId,
  setAcademicYearId,
  batchId,
  setBatchId,
  classId,
  setClassId,
  boardId,
  setBoardId,
  isArchieved,
  setIsArchieved,
  createdFrom,
  setCreatedFrom,
  createdTill,
  setCreatedTill,
  updatedFrom,
  setUpdatedFrom,
  updatedTill,
  setUpdatedTill,
  handleApplyFilter,
  handleClearFilter,
}) => {
  // Fetch all dropdown data
  const { data: deliveryData, loading: loadingDelivery } = useQuery(
    GET_ALL_EXAM_PAPERS_DELIVERY
  );
  const { data: collegeData, loading: loadingCollege } = useQuery(GET_COLLEGES);
  const { data: yearData, loading: loadingYear } = useQuery(GET_ACADEMIC_YEARS);
  const { data: batchData, loading: loadingBatch } = useQuery(GET_BATCHES);
  const { data: classData, loading: loadingClass } = useQuery(GET_CLASSES);
  const { data: boardData, loading: loadingBoard } = useQuery(GET_BOARDS);

  return (
    <Box display="flex" flexDirection="column" height="100%">
      <Box flex={1} overflow="auto" p={2}>
        <Typography variant="h6" gutterBottom>
          Filter Delivery Execution
        </Typography>

        <TextField
          select
          fullWidth
          label="Exam Paper Delivery"
          variant="outlined"
          margin="normal"
          value={examPaperDeliveryId}
          onChange={(e) => setExamPaperDeliveryId(e.target.value)}
        >
          <MenuItem value="">All</MenuItem>
          {loadingDelivery ? (
            <MenuItem disabled>
              <CircularProgress size={20} />
            </MenuItem>
          ) : (
            deliveryData?.examPaperDeliveries?.map((item: any) => (
              <MenuItem key={item.id} value={item.id}>
                {item.name}
              </MenuItem>
            ))
          )}
        </TextField>
        <TextField
          select
          fullWidth
          label="College"
          variant="outlined"
          margin="normal"
          value={collegeId}
          onChange={(e) => setCollegeId(e.target.value)}
        >
          <MenuItem value="">All</MenuItem>
          {loadingCollege ? (
            <MenuItem disabled>
              <CircularProgress size={20} />
            </MenuItem>
          ) : (
            collegeData?.colleges?.map((c: any) => (
              <MenuItem key={c.id} value={c.id}>
                {c.name}
              </MenuItem>
            ))
          )}
        </TextField>
        <TextField
          select
          fullWidth
          label="Academic Year"
          variant="outlined"
          margin="normal"
          value={academicYearId}
          onChange={(e) => setAcademicYearId(e.target.value)}
        >
          <MenuItem value="">All</MenuItem>
          {yearData?.academicYears?.map((y: any) => (
            <MenuItem key={y.id} value={y.id}>
              {y.name}
            </MenuItem>
          ))}
        </TextField>

        <TextField
          select
          fullWidth
          label="Batch"
          variant="outlined"
          margin="normal"
          value={batchId}
          onChange={(e) => setBatchId(e.target.value)}
        >
          <MenuItem value="">All</MenuItem>
          {batchData?.batches?.map((b: any) => (
            <MenuItem key={b.id} value={b.id}>
              {b.name}
            </MenuItem>
          ))}
        </TextField>

        <TextField
          select
          fullWidth
          label="Class"
          variant="outlined"
          margin="normal"
          value={classId}
          onChange={(e) => setClassId(e.target.value)}
        >
          <MenuItem value="">All</MenuItem>
          {classData?.classes?.map((cl: any) => (
            <MenuItem key={cl.id} value={cl.id}>
              {cl.name}
            </MenuItem>
          ))}
        </TextField>

        <TextField
          select
          fullWidth
          label="Board"
          variant="outlined"
          margin="normal"
          value={boardId}
          onChange={(e) => setBoardId(e.target.value)}
        >
          <MenuItem value="">All</MenuItem>
          {boardData?.boards?.map((b: any) => (
            <MenuItem key={b.id} value={b.id}>
              {b.name}
            </MenuItem>
          ))}
        </TextField>

        <TextField
          select
          fullWidth
          label="Archived"
          variant="outlined"
          margin="normal"
          value={isArchieved}
          onChange={(e) => setIsArchieved(e.target.value)}
        >
          <MenuItem value="">All</MenuItem>
          <MenuItem value="true">Yes</MenuItem>
          <MenuItem value="false">No</MenuItem>
        </TextField>

        <TextField
          fullWidth
          label="Created From"
          type="datetime-local"
          variant="outlined"
          margin="normal"
          InputLabelProps={{ shrink: true }}
          value={createdFrom}
          onChange={(e) => setCreatedFrom(e.target.value)}
        />
        <TextField
          fullWidth
          label="Created Till"
          type="datetime-local"
          variant="outlined"
          margin="normal"
          InputLabelProps={{ shrink: true }}
          value={createdTill}
          onChange={(e) => setCreatedTill(e.target.value)}
        />
        <TextField
          fullWidth
          label="Updated From"
          type="datetime-local"
          variant="outlined"
          margin="normal"
          InputLabelProps={{ shrink: true }}
          value={updatedFrom}
          onChange={(e) => setUpdatedFrom(e.target.value)}
        />
        <TextField
          fullWidth
          label="Updated Till"
          type="datetime-local"
          variant="outlined"
          margin="normal"
          InputLabelProps={{ shrink: true }}
          value={updatedTill}
          onChange={(e) => setUpdatedTill(e.target.value)}
        />
      </Box>

      <Box
        position="sticky"
        bottom={0}
        p={2}
        borderTop="1px solid #ddd"
        bgcolor="white"
        zIndex={1}
      >
        <Button
          variant="contained"
          color="primary"
          fullWidth
          onClick={handleApplyFilter}
          sx={{ mb: 1 }}
        >
          Apply Filters
        </Button>
        <Button
          variant="outlined"
          color="secondary"
          fullWidth
          onClick={handleClearFilter}
        >
          Clear Filters
        </Button>
      </Box>
    </Box>
  );
};

export default FilterInputs;
